import type {
  DashboardFreshness,
  DashboardResolveWidgetLine,
  MetricVerification,
  Provenance,
} from "@t3tools/contracts";

import { getDashboardBySlug } from "../catalog";
import type { ArtifactCell } from "./artifactTypes";

/**
 * What a number is, whether it can be trusted, how fresh it is and whether it
 * may still change, in plain words. Built from a resolve line's provenance and
 * freshness; shown in the trust popover and each widget's footer.
 */

/** Without a cadence on the line, a source table is assumed to rebuild daily. */
export const DEFAULT_CADENCE_MINUTES = 24 * 60;
/** Numbers older than this many cadences are stale. */
export const STALE_CADENCES = 2;

/** Least to most trusted; a widget reading several sources shows its least trusted. */
export const TRUST_LEVELS = ["failing", "raw", "unverified", "ad_hoc", "verified"] as const;
export type ArtifactTrustLevel = (typeof TRUST_LEVELS)[number];

export const TRUST_LABELS: Record<ArtifactTrustLevel, string> = {
  verified: "Verified",
  unverified: "Not verified yet",
  failing: "Failing its check",
  ad_hoc: "Ad-hoc",
  raw: "Raw data, unverified",
};

export type ArtifactTrustSource = {
  readonly level: ArtifactTrustLevel;
  /** The metric title, or "Ad-hoc query" for SQL. */
  readonly title: string;
  readonly definition: string;
  /** Why the level is what it is, e.g. "Last checked Oct 7, 2:00 AM". */
  readonly detail: string | null;
  readonly metricId?: string;
  readonly datasetId?: string;
};

export type ArtifactFreshnessSummary = {
  readonly computedAt: string;
  /** "Updated 6 min ago". */
  readonly text: string;
  /** Older than twice the source table's cadence. */
  readonly stale: boolean;
  readonly cadenceMinutes: number;
};

export type ArtifactSettling = { readonly reason: string; readonly from: string | null };

export type ArtifactTrust = {
  readonly level: ArtifactTrustLevel;
  readonly label: string;
  readonly sources: ReadonlyArray<ArtifactTrustSource>;
  readonly freshness: ArtifactFreshnessSummary | null;
  readonly settling: ArtifactSettling | null;
};

const checkedFormat = new Intl.DateTimeFormat("en-US", {
  timeZone: "America/New_York",
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
});

export function formatCheckedAt(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : `${checkedFormat.format(date)} ET`;
}

/** "just now", "6 min ago", "3 hr ago", "2 days ago". */
export function formatUpdatedAgo(iso: string, now: Date): string {
  const at = Date.parse(iso);
  if (Number.isNaN(at)) return "at an unknown time";
  const minutes = Math.max(0, Math.floor((now.getTime() - at) / 60_000));
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours} hr ago`;
  return `${Math.floor(hours / 24)} days ago`;
}

function verificationDetail(verified: MetricVerification): string | null {
  if (verified.status === "verified") {
    return verified.checkedAt ? `Last checked ${formatCheckedAt(verified.checkedAt)}` : null;
  }
  return verified.reason ?? null;
}

export function artifactTrustSource(provenance: Provenance): ArtifactTrustSource {
  if (provenance.kind === "metric") {
    return {
      level: provenance.verified.status,
      title: provenance.title,
      definition: provenance.description,
      detail: verificationDetail(provenance.verified),
      metricId: provenance.metricId,
    };
  }
  const raw = provenance.source === "raw_replica";
  return {
    level: raw ? "raw" : "ad_hoc",
    title: "Ad-hoc query",
    definition: provenance.definition,
    detail: raw
      ? "Read straight from the source system's copy, not from checked tables."
      : "Written for this dashboard over clean tables; not a registry metric.",
    ...(provenance.datasetId ? { datasetId: provenance.datasetId } : {}),
  };
}

export function artifactFreshness(
  freshness: DashboardFreshness,
  now: Date,
): ArtifactFreshnessSummary {
  const cadenceMinutes = freshness.cadenceMinutes ?? DEFAULT_CADENCE_MINUTES;
  const age = now.getTime() - Date.parse(freshness.computedAt);
  return {
    computedAt: freshness.computedAt,
    text: `Updated ${formatUpdatedAgo(freshness.computedAt, now)}`,
    stale: age > STALE_CADENCES * cadenceMinutes * 60_000,
    cadenceMinutes,
  };
}

export function artifactTrust(
  input: Pick<DashboardResolveWidgetLine, "provenance" | "freshness">,
  now: Date,
): ArtifactTrust {
  const sources = input.provenance.map(artifactTrustSource);
  const level =
    TRUST_LEVELS.find((candidate) => sources.some((source) => source.level === candidate)) ?? "raw";
  const freshness = input.freshness;
  return {
    level,
    label: TRUST_LABELS[level],
    sources,
    freshness: freshness ? artifactFreshness(freshness, now) : null,
    settling:
      freshness?.status === "settling"
        ? {
            reason: freshness.settlingReason ?? "Recent periods may still change.",
            from: freshness.settlingFrom ?? null,
          }
        : null,
  };
}

/** A chart point is settling when its period starts on or after `settlingFrom`. */
export function isSettlingPoint(settling: ArtifactSettling | null, x: ArtifactCell): boolean {
  if (!settling) return false;
  if (!settling.from) return true;
  return typeof x === "string" && x.slice(0, 10) >= settling.from;
}

/** One sentence for screen readers, read with the number it describes. */
export function artifactTrustDescription(trust: ArtifactTrust): string {
  return [
    trust.label,
    trust.freshness ? `${trust.freshness.text}${trust.freshness.stale ? ", stale" : ""}` : null,
    trust.settling ? `Settling: ${trust.settling.reason}` : null,
  ]
    .filter(Boolean)
    .join(". ")
    .concat(".");
}

const INCLUDE = /\{\{\s*([a-z0-9-]+)\/([a-z0-9_]+)\s*\}\}/gi;

/** Coded dashboards whose query this SQL reuses through `{{domain/name}}`. */
export function includedDashboardLabels(sql: string): string[] {
  const labels = new Set<string>();
  for (const match of sql.matchAll(INCLUDE)) {
    const domain = match[1] ?? "";
    labels.add(getDashboardBySlug(domain.toLowerCase())?.label ?? domain);
  }
  return [...labels];
}
